import React, { useState } from 'react';
import { Printer, Eye, Repeat, Clock } from 'react-feather';
import { toast } from 'react-toastify';

import { downloadFile, createFileUrl } from 'utils/appUtils';
import { formatCurrency } from 'utils/currencyUtils';
import { getFrontDate } from 'utils/dateUtils';
import PurchasesService from 'services/PurchasesService';
import TransferService from 'services/TransferService';
import TicketPreviewModal from '../TicketPreviewModal/TicketPreviewModal';
import TransferTicketModal from '../TransferTicketModal/TransferTicketModal';
import * as S from './styles';

interface EventData {
	name: string;
	startDate: string;
	customDate: string;
	fullAddress: string;
	locationType: string;
	thumbnail: string;
}

interface IndividualTicketProps {
	ticket: any;
	event: EventData | null;
	onTransferred?: () => void;
}

const IndividualTicket = ({ ticket, event, onTransferred = () => {} }: IndividualTicketProps) => {
	const [ isDownloading, setIsDownloading ] = useState(false);
	const [ isPreviewOpen, setIsPreviewOpen ] = useState(false);
	const [ isTransferOpen, setIsTransferOpen ] = useState(false);
	const [ isCancelling, setIsCancelling ] = useState(false);
	const [ showHistory, setShowHistory ] = useState(false);

	const isTransferred = ticket.transferStatus === 'TRANSFERRED';
	const isOutgoing = isTransferred && ticket.transferDirection === 'OUTGOING';

	const downloadTicket = () => {
		setIsDownloading(true);
		PurchasesService.downloadTicket(ticket.attendeeCode)
			.then(({ data }: any) => downloadFile(createFileUrl(data), 'ingresso'))
			.catch(({ data }: any) => toast(data.detail, { type: toast.TYPE.ERROR }))
			.finally(() => setIsDownloading(false))
	}

	const cancelTransfer = () => {
		setIsCancelling(true);
		TransferService.cancelTransfer(ticket.transferId)
			.then(() => {
				toast('Transferência cancelada', { type: toast.TYPE.SUCCESS });
				onTransferred();
			})
			.catch(({ data }: any) => toast(data && data.detail ? data.detail : 'Não foi possível cancelar a transferência', { type: toast.TYPE.ERROR }))
			.finally(() => setIsCancelling(false));
	}

	const transferSummary = () => {
		const counterpart = isOutgoing
			? `Transferido para ${ticket.transferredToName || '—'}`
			: `Recebido de ${ticket.transferredFromName || '—'}`;
		if (ticket.transferDate) {
			return `${counterpart} · ${getFrontDate(ticket.transferDate)}`;
		}
		return counterpart;
	}

	return (
		<S.TicketWrapper>
			<S.TicketInfo>
				<S.Attendee>{ticket.attendeeFirstName} {ticket.attendeeLastName}</S.Attendee>
				<S.Text>{ticket.ticketName} {ticket.ticketBatch}</S.Text>
				<S.Text><S.BoldText>Código:</S.BoldText> <S.Code>{ticket.attendeeCode}</S.Code></S.Text>
				<S.Text><S.BoldText>Valor:</S.BoldText> {formatCurrency(ticket.ticketPrice)}</S.Text>
				<S.Small>(taxas não inclusas)</S.Small>
			</S.TicketInfo>

				<S.TicketActions>
					{isTransferred && (
						<S.TransferredBadge data-direction={ticket.transferDirection}>
							{isOutgoing ? 'Transferido' : 'Recebido'}
						</S.TransferredBadge>
					)}

					{!isOutgoing && event && (
						<S.PreviewTicket onClick={() => setIsPreviewOpen(true)}>
							<S.Text>Visualizar</S.Text><Eye size={15} />
						</S.PreviewTicket>
					)}

					{!isOutgoing && (isDownloading
						? (<S.Downloading>Baixando</S.Downloading>)
						: (<S.DownloadTicket onClick={downloadTicket}><S.Text>Baixar</S.Text><Printer size={15} /></S.DownloadTicket>))}

					{isTransferred && (
						<S.HistoryToggle onClick={() => setShowHistory(!showHistory)}>
							<S.Text>{showHistory ? 'Ocultar histórico' : 'Histórico'}</S.Text><Clock size={14} />
						</S.HistoryToggle>
					)}

					{!isTransferred && (ticket.transferStatus === 'PENDING'
						? (
							<>
								<S.TransferBadge>Transferência pendente</S.TransferBadge>
								{ticket.transferId && (
									<S.CancelTransfer onClick={cancelTransfer} disabled={isCancelling}>
										{isCancelling ? 'Cancelando...' : 'Cancelar transferência'}
									</S.CancelTransfer>
								)}
							</>
						)
						: ticket.transferable && (
							<S.TransferTicket onClick={() => setIsTransferOpen(true)}>
								<S.Text>Transferir</S.Text><Repeat size={15} />
							</S.TransferTicket>
						))}
				</S.TicketActions>

			{isTransferred && showHistory && (
				<S.TransferLine>{transferSummary()}</S.TransferLine>
			)}

			{event && (
				<TicketPreviewModal
					isOpen={isPreviewOpen}
					onClose={() => setIsPreviewOpen(false)}
					ticket={ticket}
					event={event}
				/>
			)}

			<TransferTicketModal
				isOpen={isTransferOpen}
				ticket={ticket}
				onClose={() => setIsTransferOpen(false)}
				onTransferred={onTransferred}
			/>
		</S.TicketWrapper>
	);
}

export default IndividualTicket;
